import { getEggPoints } from 'utils/PointsAPI';
import { useMoralis } from 'react-moralis';
import { getLogin } from 'utils/API';
import { useEggBreakerPairs } from 'contexts/EggBreakerPairContext';
import { getThumbnailUrl } from 'utils/BreakerImageAPI';
import breakerplace from 'assets/img/egg/breakerplace.jpg';
import { clamp } from '../utils/Number';
import type { Stage } from './EggStageContext';
import { useQuery } from 'react-query';
import { queryKeys } from './QueryClient';
import { alchemy, collectAsync } from '../services/alchemy';
import { GRIBBLE_CONTRACT_ADDRESS } from '../utils/contract';

const NFT_PAGE_SIZE = 100;
export const PASSIVE_EGG_OMNIGROW = 100;
export const PASSIVE_PAIRED_EGG_OMNIGROW = 150;
export const PASSIVE_GRIBBLE_OMNIGROW = 100;

export type Egg = {
  id: number;
  points: number;
  paired: boolean;
  breakerSrc: string;
  favorite: boolean;
  image?: string;
};

export type Gribble = {
  id: number;
  paired: boolean;
};

export type EggLevelUpSummary = {
  id: number;
  points: number;
  pointsApplied: number;
  level: number;
  previousLevel: number;
};

export const clampedEggLevel = (eggPoints: number) => clamp(Math.floor(eggPoints / 1000) + 1, 1, 5);

export const eggImages = {
  static(level: number, stage: Stage) {
    return `/egg_assets/static/LV${level}_${stage.number}.png`;
  },
  staticFromPoints(points: number, stage: Stage) {
    const eggLevel = clampedEggLevel(points);
    return `/egg_assets/static/LV${eggLevel}_${stage.number}.png`;
  },
  detailMp4(level: number, stage: Stage) {
    return `/egg_assets/video/mp4/LV${level}_St0${stage.number}.mp4`;
  },
  detailMp4FromPoints(egg: Egg, stage: Stage) {
    const eggLevel = clampedEggLevel(egg.points);
    return `/egg_assets/video/mp4/LV${eggLevel}_St0${stage.number}.mp4`;
  },
  detailWebm(level: number, stage: Stage) {
    return `/egg_assets/video/webm/LV${level}_St0${stage.number}.webm`;
  },
  detailWebmFromPoints(egg: Egg, stage: Stage) {
    const eggLevel = clampedEggLevel(egg.points);
    return `/egg_assets/video/webm/LV${eggLevel}_St0${stage.number}.webm`;
  },
};

export const getPassivePoints = (paired: boolean) =>
  paired ? PASSIVE_EGG_OMNIGROW + PASSIVE_PAIRED_EGG_OMNIGROW : PASSIVE_EGG_OMNIGROW;

// This returns all of a users gribbles, including unhatched eggs
export async function getGribbles(address: string) {
  return collectAsync(
    alchemy.nft.getNftsForOwnerIterator(address, {
      contractAddresses: [GRIBBLE_CONTRACT_ADDRESS],
      pageSize: NFT_PAGE_SIZE,
    }),
  );
}

type EggResponse = {
  eggs: Egg[];
  gribbles: Gribble[];
};

export const useEggContext = () => {
  const { pairs, requestCompleted } = useEggBreakerPairs();
  const { user, isInitialized } = useMoralis();
  const ethAddress = user?.attributes?.authData?.moralisEth?.id;

  const query = useQuery<EggResponse>(
    queryKeys.eggs(ethAddress),
    async () => {
      const result = await getGribbles(ethAddress);
      const userId = getLogin()?.userId;
      const gribbleTokens = result.map(({ tokenId }) => parseInt(tokenId, 10));

      const eggPoints = await getEggPoints(userId!, gribbleTokens);

      const eggs = eggPoints.map(({ gribbleTokenId, points, favorite }) => {
        const pairedBreaker = pairs.find(({ gribbleId }) => gribbleId === gribbleTokenId);

        return {
          id: gribbleTokenId,
          paired: pairedBreaker !== undefined,
          breakerSrc: pairedBreaker ? getThumbnailUrl(pairedBreaker.breakerId) : breakerplace,
          points,
          favorite,
        };
      });

      const gribbles = gribbleTokens
        .filter((tokenId) => {
          // Remove any tokens that are still eggs
          return !eggPoints.find((egg) => egg.gribbleTokenId === tokenId);
        })
        .map((tokenId) => {
          const pairedBreaker = pairs.find(({ gribbleId }) => gribbleId === tokenId);

          return {
            id: tokenId,
            paired: pairedBreaker !== undefined,
          };
        });

      return {
        eggs,
        gribbles,
      };
    },
    {
      enabled: isInitialized && requestCompleted,
      // store the user's eggs for 1 minutes
      cacheTime: 1000 * 60,
      // only refetch after 1 minute
      staleTime: 1000 * 60,
      refetchOnWindowFocus: false,
    },
  );

  return {
    ...query,
    ethAddress,
  };
};

export type EggLevel = 1 | 2 | 3 | 4 | 5;
